import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../store";
import { userActions } from "../slices/user";
import { UserMapper } from "../../mappers/UserMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { saveUser } from "../../lib/storage";
import { UserCreateRequestDto } from "../../shared/dtos/user/UserCreateRequestDto";
import { UserResponseDto } from "../../shared/dtos/user/UserResponseDto";
import { UserSummaryDto } from "../../shared/dtos/user/UserSummaryDto";
import { UserQueryDto } from "../../shared/dtos/user/UserQueryDto";
import { UpdateUserPayload } from "../../shared/interfaces/user/UpdateUserPayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getUsers =
  (params?: UserQueryDto) => async (dispatch: AppDispatch) => {
    dispatch(userActions.setLoadingFetchAll());

    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<UserSummaryDto>>
      >("/users", {
        headers: { requiresAuth: true },
        params: params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(userActions.setItems(data.data.content));
        dispatch(userActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(userActions.setErrorFetchAll(message));
    }
  };

export const getUserById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(userActions.setLoadingFetchOne());

  try {
    const { data } = await axiosInstance.get<ApiResponseDto<UserResponseDto>>(
      `/users/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(userActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(userActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const createUser =
  (values: UserCreateRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(userActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<UserResponseDto>
      >("/users", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "User created successfully!");

        const summary = UserMapper.mapUserToSummary(data.data);
        dispatch(userActions.createSuccess(summary));
        dispatch(
          userActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(userActions.setErrorCreate(message));
    }
  };

export const updateUser =
  (payload: UpdateUserPayload) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(userActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<ApiResponseDto<UserResponseDto>>(
        `/users/${payload.id}`,
        payload.values,
        { headers: { requiresAuth: true } },
      );

      const { userInfo } = getState().auth; // Access session user from Auth slice

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Profile updated successfully!");

        const detailedUser = data.data;

        // If the user being updated is the one currently logged in, sync session storage
        if (userInfo && userInfo.id === payload.id) {
          saveUser(detailedUser);
        }

        const summary = UserMapper.mapUserToSummary(detailedUser);
        dispatch(userActions.updateSuccess(summary));

        dispatch(
          userActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(userActions.setErrorUpdate(message));
    }
  };

export const deleteUser = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(userActions.setLoadingDelete());

  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/users/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "User deleted successfully!");
      dispatch(userActions.deleteSuccess(id));
      dispatch(
        userActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(userActions.setErrorDelete(message));
  }
};

export const resetUserServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(userActions.setServerStatus(null));
};
